// Which hour columns the utilization grid draws, and what each one holds.
//
// Both rules here exist because the grid got them wrong:
//
//  1. **A column shows the peak of its hour.** It must never read free for an
//     hour that is partly booked, so an hour takes the worst moment inside it
//     rather than its opening value — the same rule the backend applies one
//     level down when it buckets the occupancy step function into cells
//     (new-backend/admin/src/lib/booking/occupancyGrid.ts).
//
//  2. **Every hour that carries occupancy is drawn.** The grid used to draw a
//     fixed 06:00–22:00 window while the row's peak counted the whole day, so a
//     booking running 22:30–05:30 rendered its second day as an empty row with
//     a peak of "5 / 5" — the row contradicting itself. Storage is occupied
//     around the clock by nature, and product rental runs overnight, so this is
//     the normal case there rather than an edge one.
//
// The default window stays the floor: for a station whose bookings all sit
// inside the working day (SUP, hourly rental) the grid looks exactly as before,
// and it only grows when there is something outside it to show.

/** First hour drawn when nothing outside the working day is booked. */
export const DEFAULT_FIRST_HOUR = 6;
/** Last hour drawn when nothing outside the working day is booked. */
export const DEFAULT_LAST_HOUR = 22;

const HOURS_IN_DAY = 24;

/** One display slot, as the occupancy endpoint returns it. */
export interface HourlyCell {
    from: string;
    used: number;
    /**
     * Is any part of this slot NOT firm? (#2634 item 3)
     *
     * Optional: an older cached bundle reading a newer response is not the
     * risk here, but a newer bundle reading an OLDER cached response is, and
     * absent must mean "firm" rather than `undefined` leaking into a class
     * name.
     */
    hasReserved?: boolean;
}

/**
 * Peak `used` per hour of day, indexed 0–23.
 *
 * `hourOf` reads the hour in the *display* timezone rather than the browser's:
 * the backend groups cells into days in Europe/Berlin, so deriving the hour
 * from the viewer's clock would file a cell under one day's row and another
 * day's column for anyone outside that zone.
 *
 * Bucketing once per day also replaces a scan of every cell per hour — at the
 * 31-day tab that was 24 passes over 96 cells for each of 31 rows.
 */
export const peaksByHour = (
    cells: HourlyCell[],
    hourOf: (iso: string) => number,
): number[] => {
    const peaks = new Array<number>(HOURS_IN_DAY).fill(0);

    cells.forEach((cell) => {
        const hour = hourOf(cell.from);
        if (!Number.isInteger(hour) || hour < 0 || hour >= HOURS_IN_DAY) return;
        if (cell.used > (peaks[hour] as number)) peaks[hour] = cell.used;
    });

    return peaks;
};

/**
 * Which hours carry an unfirm block, indexed 0–23 (#2634 item 3).
 *
 * A separate pass rather than a field on `peaksByHour`, and ANY rather than a
 * peak: the two reduce differently. An hour's busiest cell can be firm while a
 * quieter one is not — two paid bookings followed by one unaccepted offer —
 * and taking the peak's own firmness would drop exactly the block the operator
 * needs to see. One unfirm minute in the hour makes the hour unfirm.
 *
 * Bucketed in the same single pass shape as `peaksByHour`, for the same
 * reason: at the 31-day tab a per-hour scan was 24 passes over 96 cells per
 * row.
 */
export const reservedByHour = (
    cells: HourlyCell[],
    hourOf: (iso: string) => number,
): boolean[] => {
    const reserved = new Array<boolean>(HOURS_IN_DAY).fill(false);

    cells.forEach((cell) => {
        if (cell.hasReserved !== true) return;
        const hour = hourOf(cell.from);
        if (!Number.isInteger(hour) || hour < 0 || hour >= HOURS_IN_DAY) return;
        reserved[hour] = true;
    });

    return reserved;
};

/**
 * The hour columns to draw: the default window, widened on either side to cover
 * every hour that actually carries occupancy.
 *
 * `daysPeaks` is one `peaksByHour` array per day row. The window is contiguous
 * — an occupied 03:00 pulls the whole range down to 3 rather than adding a
 * detached column, because a gap in an hour axis reads as missing data.
 */
export const visibleHours = (
    daysPeaks: number[][],
    firstHour: number = DEFAULT_FIRST_HOUR,
    lastHour: number = DEFAULT_LAST_HOUR,
): number[] => {
    let from = firstHour;
    let to = lastHour;

    daysPeaks.forEach((peaks) => {
        peaks.forEach((used, hour) => {
            if (used <= 0) return;
            if (hour < from) from = hour;
            if (hour > to) to = hour;
        });
    });

    return Array.from({ length: to - from + 1 }, (_, index) => from + index);
};
